/**
 * Row and bulk actions for the Call Off Management list — send, cancel and
 * bulk confirm, with the confirmation-dialog state they share.
 *
 * Split out of CallOffManagementTab (CR-7717) to keep that component under the
 * 500-line limit. Behaviour is unchanged: every action reloads the list on
 * success and surfaces the API's own message on failure.
 */
import { useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import { toast } from 'sonner';

export type CallOffBulkAction = 'send' | 'cancel' | 'confirm';

export interface CallOffListActions {
  /** The bulk action awaiting confirmation (null = dialog closed). */
  bulkAction: CallOffBulkAction | null;
  setBulkAction: (action: CallOffBulkAction | null) => void;
  actionLoading: boolean;
  executeBulkAction: () => Promise<void>;
  handleSingleSend: (id: number, skipValidation?: boolean) => Promise<void>;
  handleSingleCancel: (id: number) => Promise<void>;
}

/**
 * Send / cancel / bulk-confirm handlers for the call-off list.
 *
 * @example
 * const actions = useCallOffListActions(selectedIds, fetchData);
 * <Button onClick={() => actions.setBulkAction('send')}>Send</Button>
 *
 * @param selectedIds - The call-off ids currently ticked for a bulk action
 * @param reload - Reloads the list; called after every successful action
 * @returns The dialog state and the action handlers
 */
export function useCallOffListActions(
  selectedIds: Set<number>,
  reload: () => void,
): CallOffListActions {
  const [bulkAction, setBulkAction] = useState<CallOffBulkAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const executeBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;

    // Same mandatory reason as the single cancel — collected once and applied
    // to the whole selection. Prompt before the loading state so cancelling
    // the prompt leaves the buttons responsive.
    let cancelReason = '';
    if (bulkAction === 'cancel') {
      const reason = window.prompt(
        `Reason for cancelling ${selectedIds.size} call-off(s) (recorded on each and visible to the supplier if already sent):`
      );
      if (reason === null) return;
      cancelReason = reason.trim();
      if (!cancelReason) {
        toast.error('A cancellation reason is required.');
        return;
      }
    }

    setActionLoading(true);
    try {
      const ids = Array.from(selectedIds);
      if (bulkAction === 'confirm') {
        const res = await callOffAPI.bulkConfirm(ids);
        const confirmed = res.data.confirmed || 0;
        const errors = res.data.errors || [];
        if (errors.length > 0) {
          toast.success(`${confirmed} call-off(s) confirmed, ${errors.length} failed`);
        } else {
          toast.success(`${confirmed} call-off(s) confirmed successfully`);
        }
        // Same warning the single confirm raises. Without it a bulk confirm
        // reported plain success for a call-off whose delivery lines covered
        // less than was asked for — or that got none at all.
        const shortfalls = res.data.allocation_shortfalls || [];
        if (shortfalls.length > 0) {
          toast.warning(
            `PO coverage fell short — delivery lines cover the available quantity only: ` +
            shortfalls.map(s => `${s.reference} ${s.part_number} short ${s.shortfall}`).join(', '),
            { duration: 10000 }
          );
        }
      } else {
        const promises = ids.map((id) =>
          bulkAction === 'send'
            ? callOffAPI.sendCallOff(id, true)
            : callOffAPI.cancelCallOff(id, cancelReason)
        );
        await Promise.all(promises);
        toast.success(`${ids.length} call-off(s) ${bulkAction === 'send' ? 'sent' : 'cancelled'} successfully`);
      }
      reload();
    } catch (err: unknown) {
      // Prefer the API's message so a rejected bulk action says why.
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr.response?.data?.message ||
        (err instanceof Error ? err.message : `Failed to ${bulkAction} call-offs`);
      toast.error(message);
    } finally {
      setActionLoading(false);
      setBulkAction(null);
    }
  };

  const handleSingleSend = async (id: number, skipValidation = false) => {
    try {
      await callOffAPI.sendCallOff(id, skipValidation);
      toast.success('Call-off sent to supplier');
      reload();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { code?: string; message?: string } } };
      if (axiosErr.response?.data?.code === 'insufficient_po_coverage') {
        const override = window.confirm(
          `${axiosErr.response.data.message || 'Insufficient PO coverage.'}\n\nDo you want to send anyway without PO validation?`
        );
        if (override) {
          handleSingleSend(id, true);
          return;
        }
      } else {
        const message = axiosErr.response?.data?.message || (err instanceof Error ? err.message : 'Failed to send call-off');
        toast.error(message);
      }
    }
  };

  const handleSingleCancel = async (id: number) => {
    // A cancellation reason is mandatory for every state, so collect one here
    // the way CallOffDetailModal does. Posting an empty reason made this
    // button fail with a 400 every time, with no way to satisfy it.
    const reason = window.prompt(
      'Reason for cancelling this call-off (recorded on the call-off and visible to the supplier if already sent):'
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error('A cancellation reason is required.');
      return;
    }
    try {
      await callOffAPI.cancelCallOff(id, trimmed);
      toast.success('Call-off cancelled');
      reload();
    } catch (err: unknown) {
      // Surface the API's message, as handleSingleSend does. Falling back to
      // the axios Error turned a "cancellation reason is required" 400 into a
      // bare "Request failed with status code 400" with nothing to act on.
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to cancel call-off');
      toast.error(message);
    }
  };

  return {
    bulkAction,
    setBulkAction,
    actionLoading,
    executeBulkAction,
    handleSingleSend,
    handleSingleCancel,
  };
}
